import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, model } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { BaasAppState } from '@lvflanders/bar';

import { BaasActionButton } from '../../../baas-action-button/baas-action-button';
import { BaasActionButtonOptions } from '../../../baas-action-button/baas-action-button.types';
import { _BaasInternalLayoutService } from '../../../layout/baas-internal-layout.service';
import { BaasExternalLinkGroupOptions } from '../../baas-external-link-group-options.type';
import { BarMenuState } from '../../bar-menu-state.type';
import { BaasLayoutService } from '../baas-layout.service';
import { BaasLayoutMenuExternalLinksComponent } from './baas-layout-menu-external-links/baas-layout-menu-external-links.component';
import { BaasLayoutMenuListComponent } from './baas-layout-menu-list/baas-layout-menu-list.component';
import { BaasLayoutMenuOperationsComponent } from './baas-layout-menu-operations/baas-layout-menu-operations.component';
import { BaasLayoutMenuStrikingActionsComponent } from './baas-layout-menu-striking-actions/baas-layout-menu-striking-actions.component';
import { BaasMenuItemOptions } from './baas-menu-item-options.type';

type BaasEnrichedMenuItemOptions = BaasMenuItemOptions & {
    /** internal route segments array used to compare routes for the expansion logic */
    _routeSegments?: string[];
};

@Component({
    selector: 'baas-layout-menu',
    templateUrl: './baas-layout-menu.component.html',
    host: { class: 'bar-layout-menu-root' },
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgTemplateOutlet,
        BaasLayoutMenuOperationsComponent,
        BaasLayoutMenuExternalLinksComponent,
        BaasLayoutMenuListComponent,
        BaasLayoutMenuStrikingActionsComponent,
        BaasActionButton
    ]
})
export class BaasLayoutMenuComponent {
    readonly #router = inject(Router);
    readonly internalLayout = inject(_BaasInternalLayoutService);
    readonly #layout = inject(BaasLayoutService);
    readonly #state = inject(BaasAppState, { optional: true });

    readonly mobileFriendly = input<boolean>(undefined);

    readonly menuState = model<BarMenuState>(undefined);

    readonly operations = input<BaasActionButtonOptions[]>(undefined);
    readonly strikingActions = input<BaasActionButtonOptions[]>(undefined);
    readonly externalLinkGroups = input<BaasExternalLinkGroupOptions[]>(undefined);
    readonly menuAction = input<BaasActionButtonOptions>(undefined);
    readonly menuItems = input<BaasMenuItemOptions[]>(undefined);
    readonly menuItemsAutoCollapse = input<boolean>(undefined);
    readonly hideActionBarContentLeft = input<boolean>(undefined);

    protected readonly effectiveMenuItems = linkedSignal(() => {
        let menuItems = this.menuItems() || this.#layout.menuItems() || [];
        if (this.menuItemsAutoCollapse()) {
            // auto mechanism to collapse inactive expanded submenus
            menuItems = this.#closeInactiveSubmenus(menuItems);
        }
        return this.#processMenuItems(menuItems);
    });

    protected readonly mainAction = computed(() => this.#layout.mainAction() || this.internalLayout.mainAction());
    protected readonly menuToggleMetaIcon = computed(() => this.#stateMapping[this.menuState() || 'default'].icon);
    protected readonly menuToggleMetaText = computed(() => this.#stateMapping[this.menuState() || 'default'].text);
    protected readonly smallDevice = this.internalLayout.smallDevice;

    readonly #stateMapping = {
        closed: {
            icon: 'fas fa-caret-right',
            text: 'Menu openen',
            action: () => {
                this.menuState.set('overlay');
            }
        },
        pinned: {
            icon: 'fas fa-caret-left',
            text: 'Menu sluiten',
            action: () => {
                this.menuState.set(this.mobileFriendly() ? 'overlay' : 'closed');
            }
        },
        overlay: {
            icon: 'fas fa-caret-right',
            text: 'Menu pinnen',
            action: () => {
                this.menuState.set('pinned');
            }
        },
        default: {
            icon: this.smallDevice() ? 'fas fa-caret-right' : 'fas fa-caret-left',
            text: this.smallDevice() ? 'Menu pinnen' : 'Menu sluiten',
            action: () => {
                if (this.smallDevice()) {
                    this.menuState.set('pinned');
                } else {
                    this.menuState.set(this.mobileFriendly() ? 'overlay' : 'closed');
                }
            }
        }
    };

    constructor() {
        this.#router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
            if (event instanceof NavigationEnd && this.menuItemsAutoCollapse()) {
                // close inactive submenu's
                const collapsedMenus = this.#closeInactiveSubmenus(this.menuItems());
                // set new menu (only active tree is expanded)
                this.effectiveMenuItems.set(this.#processMenuItems(collapsedMenus));
            }
        });
    }

    protected menuToggleClicked(event) {
        this.#stateMapping[this.menuState() || 'default'].action();
    }

    protected setMenuState(menuState: BarMenuState): void {
        this.menuState.set(menuState);
    }

    /** Sets the items expand property to true if a subItem is active, if not to false */
    #setExpandedStateForActiveItems(items: BaasMenuItemOptions[]): BaasMenuItemOptions[] {
        return items.map((item: BaasEnrichedMenuItemOptions) => {
            // only set expanded items for the current application
            if (!item.appCode || item.appCode === this.#state.toepassing.code) {
                // first check if expanded has been preconfigured, if so skip checking
                if (!item.expanded) {
                    if (item._routeSegments) {
                        let hasActiveSubChild: boolean;

                        if (item.children && item.children.length) hasActiveSubChild = this.#hasSubItemWithActiveRoute(item.children);

                        item.expanded = hasActiveSubChild || this.#checkRouteMatch(item._routeSegments);
                    } else if (item.children && item.children.length) {
                        item.expanded = this.#hasSubItemWithActiveRoute(item.children);
                    }
                }
                // if expanded, continue checking the node's tree to expand further
                if (item.expanded && item.children && item.children.length) this.#setExpandedStateForActiveItems(item.children);
            }

            return item;
        });
    }

    /** Check if an item with children has an active routerLink
     * return true if a subChild is active (can be nested inifitely deep)
     * return false if no subChild is active
     **/
    #hasSubItemWithActiveRoute(items: BaasMenuItemOptions[]) {
        const itemHasActiveSubItem: any = items.find((subItem: BaasEnrichedMenuItemOptions) => {
            if (subItem._routeSegments) {
                // when routerlink is in pathname always return true
                const match = this.#checkRouteMatch(subItem._routeSegments);
                if (match) return true;
            }
            // no router path
            // or router path is not in the pathname
            // then continue checking the node's tree
            if (subItem.children && subItem.children.length) return this.#hasSubItemWithActiveRoute(subItem.children);

            return false;
        });

        return !!itemHasActiveSubItem;
    }

    #checkRouteMatch(routeSegments: string[]): boolean {
        // Split both paths into segments, removing empty segments
        const currentRouteSegments = this.#router.url
            // cut out query string
            .split('?')[0]
            .split('/')
            .filter((segment) => segment !== '');

        // Check if pathname segments match from the beginning of location segments
        if (routeSegments.length > currentRouteSegments.length) return false;

        return routeSegments.every((segment, index) => segment === currentRouteSegments[index]);
    }

    /**
     * Auto mechanism to collapse inactiv expanded submenus
     * We set all the expanded properties to false
     * Due to the subscription of menuItems$ the setExpandedStateForActiveItems method will be called and will set only the active sub menu to an active state
     */
    #closeInactiveSubmenus(menuItems): BaasMenuItemOptions[] {
        if (!menuItems) return [];
        return menuItems.map((item: BaasMenuItemOptions): BaasMenuItemOptions => {
            item.expanded = false;
            if (item.children && item.children.length) item.children = this.#closeInactiveSubmenus(item.children);
            return item;
        });
    }

    #getPath(path, index) {
        return path.length ? `${path}.${index}` : `${index}`;
    }

    #findMenuItemRecursive(menuItems: BaasMenuItemOptions[], path): BaasMenuItemOptions {
        for (let index = 0; index < menuItems.length; index++) {
            const menuItem = menuItems[index];

            if (menuItem.routerCommands) {
                const urlTree = this.#router.createUrlTree(menuItem.routerCommands);
                if (this.#router.isActive(urlTree, true)) {
                    // active item found, set it expanded (does not mather if doens't have child items)
                    menuItem.expanded = true;
                    return menuItem;
                }
            }

            if (menuItem.children && menuItem.children.length) {
                const subMenuItem = this.#findMenuItemRecursive(menuItem.children, this.#getPath(path, index));
                if (subMenuItem) return subMenuItem;
            }
        }

        return null;
    }

    #processMenuItems(menuItems: BaasMenuItemOptions[]): BaasMenuItemOptions[] {
        // do some computations once for performance
        menuItems = this.#enrichMenuItems(menuItems);

        if (menuItems?.length) {
            menuItems = this.#setExpandedStateForActiveItems(menuItems);
            this.#findMenuItemRecursive(menuItems, '');
        } else menuItems = [];

        return menuItems;
    }

    #enrichMenuItems(menuItems: BaasEnrichedMenuItemOptions[]): BaasMenuItemOptions[] {
        return menuItems.map((item) => {
            if (item.routerUrl) item._routeSegments = item.routerUrl.split('/').filter((segment) => segment !== '');
            else if (item.routerCommands) {
                // Convert router commands to URL tree and serialize to get the final router URL (without ./ or ../ or /)
                const urlTree = this.#router.createUrlTree(item.routerCommands);
                const routerUrl = this.#router.serializeUrl(urlTree);
                // Remove query params and fragment, then split into segments
                item._routeSegments = routerUrl
                    .split('?')[0]
                    .split('#')[0]
                    .split('/')
                    .filter((segment) => segment !== '');
            } else item._routeSegments = undefined;
            if (item.children?.length) item.children = this.#enrichMenuItems(item.children);
            return item;
        });
    }
}
